// SimplifiedMapWithSearch.js
import React, { useState, useRef, useEffect } from 'react';
import { MapPin, Search, XCircle, Compass } from 'lucide-react';
 
const SimplifiedMapWithSearch = ({
  latitude,
  longitude,
  onCoordinateChange,
  onCoordinatesChange = null,
  disabled = false,
  initialShowMap = false,
  initialSearchTerm = '',
  autoApplyCoordinates = false
}) => {
  const [showMap, setShowMap] = useState(initialShowMap);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const mapContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const leafletRef = useRef(null); // Reference to the Leaflet library

  const applyBothCoordinates = (lat, lng) => {
    if (disabled) return;
    if (typeof onCoordinatesChange === 'function') {
      onCoordinatesChange(lat.toString(), lng.toString());
      return;
    }
    onCoordinateChange('latitude', lat.toString());
    onCoordinateChange('longitude', lng.toString());
  };

  // Load Leaflet map library using ES modules
  useEffect(() => {
    if (showMap && !mapLoaded) {
      const loadLeaflet = async () => {
        try {
          // Load Leaflet CSS if not already loaded
          if (!document.getElementById('leaflet-css')) {
            const cssLink = document.createElement('link');
            cssLink.id = 'leaflet-css';
            cssLink.rel = 'stylesheet';
            cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(cssLink);
          }

if (!leafletRef.current) {
  if (window.L) {
    leafletRef.current = window.L;
    setMapLoaded(true);
    return;
  }

  const handleLeafletLoaded = () => {
    leafletRef.current = window.L;
    console.log('Leaflet loaded successfully via script');
    setMapLoaded(true);
  };
  const existingScript = document.getElementById('leaflet-script');
  if (existingScript) {
    existingScript.addEventListener('load', handleLeafletLoaded, { once: true });
    return;
  }

  const script = document.createElement('script');
  script.id = 'leaflet-script';
  script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  script.onload = handleLeafletLoaded;
  document.head.appendChild(script);
  return; // Exit early, let onload handle setMapLoaded
}
          
          setMapLoaded(true);
        } catch (error) {
          console.error('Error loading Leaflet:', error);
          setLoadError('Failed to load map library. Please try again later.');
        }
      };

      loadLeaflet();
    }
  }, [showMap]);

  // Initialize map when libraries are loaded and modal is open
  useEffect(() => {
    if (showMap && mapLoaded && leafletRef.current) {
      setTimeout(initMap, 100); // Small delay to ensure DOM is ready
    }
  }, [showMap, mapLoaded]);

  const initMap = () => {
    // Only initialize if the map container exists and is in the DOM
    if (!mapContainerRef.current || !leafletRef.current) {
      console.warn("Map container not found or Leaflet not loaded");
      return;
    }
    
    const L = leafletRef.current;
    
    // If a map instance already exists, destroy it first
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
  
    // Parse coordinates, defaulting to Lisbon if invalid
    const lat = parseFloat(latitude) || 38.7223;
    const lng = parseFloat(longitude) || -9.1393;
    
    console.log("Initializing Leaflet map with:", { lat, lng });
  
    try {
      // Create the map with Leaflet
      const map = L.map(mapContainerRef.current).setView([lat, lng], 15);
      mapInstanceRef.current = map;
      
      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(map);
      
      // Add a marker
      const marker = L.marker([lat, lng], {
        draggable: true
      }).addTo(map);
      markerRef.current = marker;
      
      // Handle map click to move marker
      map.on('click', function(e) {
        const clickLat = e.latlng.lat;
        const clickLng = e.latlng.lng;
        console.log("Map click at:", { lat: clickLat, lng: clickLng });
        marker.setLatLng(e.latlng);
        if (autoApplyCoordinates) {
          applyBothCoordinates(clickLat, clickLng);
        }
      });

      marker.on('dragend', function(e) {
        if (!autoApplyCoordinates) return;
        const position = e.target.getLatLng();
        applyBothCoordinates(position.lat, position.lng);
      });

      // Map is ready
      mapRef.current = map;
    } catch (error) {
      console.error("Error initializing map:", error);
      setLoadError("Failed to initialize map. Please try again.");
    }
  };

 

  // Function for reverse geocoding with Nominatim API (OpenStreetMap)
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.display_name) {
          setSearchTerm(data.display_name);
        } else {
          setSearchTerm(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        }
      } else {
        console.warn('Reverse geocoding failed, using coordinates');
        setSearchTerm(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
      }
    } catch (error) {
      console.error('Error in reverse geocoding:', error);
      setSearchTerm(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    }
  };

  // Function to handle search form submission with Nominatim API
  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim() || !mapRef.current || !markerRef.current) return;
    
    try {
      // Search using Nominatim API
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&addressdetails=1&q=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          const result = data[0];
          const lat = parseFloat(result.lat);
          const lon = parseFloat(result.lon);
          
          // Update marker and map position
          markerRef.current.setLatLng([lat, lon]);
          mapRef.current.setView([lat, lon], 16);
          if (autoApplyCoordinates) {
            applyBothCoordinates(lat, lon);
          }
        } else {
          console.warn('No results found for search term:', searchTerm);
        }
      } else {
        console.warn('Search failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error during search:', error);
    }
  };

  // Function to get current device location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          if (mapRef.current && markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
            mapRef.current.setView([lat, lng], 16);
            if (autoApplyCoordinates) {
              applyBothCoordinates(lat, lng);
            }
            reverseGeocode(lat, lng);
          }
        },
        (error) => {
          console.error('Error getting current location:', error);
          alert('Unable to retrieve your location. Please search or click on the map.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };
  
  const extractLatitude = () => {
    if (!markerRef.current) return;
    
    const position = markerRef.current.getLatLng();
    const lat = position.lat;
    console.log("Extracting latitude:", lat);
    onCoordinateChange('latitude', lat.toString());
    
    // Update the search term with the reverse geocoded address
    reverseGeocode(lat, position.lng);
  };
  
  const extractLongitude = () => {
    if (!markerRef.current) return;
    
    const position = markerRef.current.getLatLng();
    const lng = position.lng;
    console.log("Extracting longitude:", lng);
    onCoordinateChange('longitude', lng.toString());
    
    // Update the search term with the reverse geocoded address
    reverseGeocode(position.lat, lng);
  };
  
  const extractBoth = () => {
    if (!markerRef.current) return;
    
    const position = markerRef.current.getLatLng();
    const lat = position.lat;
    const lng = position.lng;
    console.log("Extracting both coordinates:", { lat, lng });
    
    applyBothCoordinates(lat, lng);
    
    // Update the search term with the reverse geocoded address
    reverseGeocode(lat, lng);
  };

  return (
    <div className="w-full mt-2">
      <div className="flex justify-between mb-2">
        <div className="text-sm text-gray-600">
          Click on map to place marker
        </div>
        <button
          onClick={() => !disabled && setShowMap(!showMap)}
          className={`px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={disabled}
        >
          <MapPin className="w-4 h-4" />
          {disabled ? 'View Only' : (showMap ? 'Hide Map' : 'Show Map')}
        </button>
      </div>
      
      {showMap && (
        <>
          {/* Display any load errors */}
          {loadError && (
            <div className="mb-2 p-2 bg-red-50 border border-red-200 text-red-600 rounded">
              {loadError}
            </div>
          )}
          
          {/* Search bar */}
          <div className={`flex items-center bg-gray-50 border p-2 rounded-t ${disabled ? 'opacity-60' : ''}`}>
            <form onSubmit={disabled ? (e) => e.preventDefault() : handleSearchSubmit} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                  <Search className="w-4 h-4 text-gray-400" />
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => !disabled && setSearchTerm(e.target.value)}
                  placeholder={disabled ? "View Only Mode" : "Search for a location..."}
                  className={`w-full pl-8 pr-3 py-2 border rounded-md ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  disabled={disabled}
                  readOnly={disabled}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => !disabled && setSearchTerm('')}
                    className={`absolute inset-y-0 right-0 flex items-center pr-2 ${disabled ? 'cursor-not-allowed' : ''}`}
                    disabled={disabled}
                  >
                    <XCircle className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className={`px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={disabled}
              >
                Search
              </button>
              <button
                type="button"
                onClick={disabled ? () => {} : getCurrentLocation}
                className={`px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 flex items-center gap-1 text-gray-700 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={disabled ? "View Only Mode" : "Use my current location"}
                disabled={disabled}
              >
                <Compass className="w-4 h-4" />
              </button>
            </form>
          </div>
          
          {/* Map container */}
          <div 
            ref={mapContainerRef} 
            className="w-full border-x border-b rounded-b"
            style={{ height: "350px" }}
          ></div>
          
          {/* Coordinates display */}
          {markerRef.current && (
            <div className="mt-2 mb-2 text-sm text-gray-700 bg-gray-50 p-2 rounded border">
              Current marker position: 
              <span className="font-mono ml-1">
                {markerRef.current.getLatLng().lat.toFixed(6)}, {markerRef.current.getLatLng().lng.toFixed(6)}
              </span>
            </div>
          )}
          
          {/* Extract buttons */}
          {!autoApplyCoordinates && (
            <div className={`flex gap-2 mt-2 ${disabled ? 'opacity-60' : ''}`}>
              <button
                onClick={disabled ? () => {} : extractLatitude}
                className={`flex-1 px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1 ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                disabled={disabled}
              >
                <MapPin className="w-4 h-4" />
                Extract Latitude
              </button>

              <button
                onClick={disabled ? () => {} : extractLongitude}
                className={`flex-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center gap-1 ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                disabled={disabled}
              >
                <MapPin className="w-4 h-4 rotate-90" />
                Extract Longitude
              </button>

              <button
                onClick={disabled ? () => {} : extractBoth}
                className={`flex-1 px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center justify-center gap-1 ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                disabled={disabled}
              >
                <MapPin className="w-4 h-4" />
                Extract Both
              </button>
            </div>
          )}
          
          {/* Usage instructions */}
          <div className="mt-3 text-xs text-gray-500 bg-gray-50 p-2 rounded">
            <p>📍 <strong>How to use:</strong> {autoApplyCoordinates ? 'Search, click the map, or drag the marker; both coordinates are saved immediately.' : 'Click on the map to place a marker at the exact location, then click "Extract" to use those coordinates.'}</p>
            <p>🔍 <strong>Search:</strong> Enter a location name to search and place the marker at that location.</p>
          </div>
        </>
      )}
    </div>
  );
};

export default SimplifiedMapWithSearch;
